import {Box, Typography} from '@mui/material';
import type {FC} from 'react';
import {
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  ReferenceManyField,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
} from 'react-admin';
import {InventoryStemDatagrid} from '@/admin/resources/inventoryStems/InventoryStems';
import {SlamPointCloudDatagrid} from '@/admin/resources/slamPointClouds/SlamPointClouds';

const STATUS_CHOICES = [
  {id: 'DATA_PENDING', name: 'DATA_PENDING'},
  {id: 'DATA_UPLOADED', name: 'DATA_UPLOADED'},
];

const ToolbarDeleteOnly: FC = (props) => (
  <Toolbar {...props}>
    <DeleteButton />
  </Toolbar>
);

const toGeoJson = (data: Record<string, unknown>) => {
  const coords = (data.position as {coordinates?: number[]})?.coordinates;
  return {
    ...data,
    position: {
      type: 'Point' as const,
      coordinates: [coords?.[0] ?? 0, coords?.[1] ?? 0],
    },
  };
};

export const InventoryPlotEdit: FC = () => (
  <Edit transform={toGeoJson}>
    <SimpleForm toolbar={<ToolbarDeleteOnly />}>
      <ReferenceInput source="forestMassSpeciesUuid" reference="forest-mass-species">
        <SelectInput optionText="code" validate={[required()]} />
      </ReferenceInput>
      <TextInput source="code" validate={[required()]} />
      <SelectInput source="status" choices={STATUS_CHOICES} validate={[required()]} />
      <NumberInput source="diameter" validate={[required()]} />
      <NumberInput source="hrms" validate={[required()]} />
      <NumberInput source="vrms" validate={[required()]} />
      <NumberInput source="altitude" validate={[required()]} />
      <NumberInput source="utmX" validate={[required()]} />
      <NumberInput source="utmY" validate={[required()]} />
      <NumberInput source="position.coordinates.0" label="Longitude" />
      <NumberInput source="position.coordinates.1" label="Latitude" />
      <NumberInput source="appraiserWeight" />
      <NumberInput source="volumeUsc" />
      <NumberInput source="volumeIfn" />
    </SimpleForm>
    <Box mt={2}>
      <Typography variant="h4">SLAM Point Clouds</Typography>
      <ReferenceManyField reference="slam-point-clouds" target="inventoryPlotUuid">
        <SlamPointCloudDatagrid />
      </ReferenceManyField>
    </Box>
    <Box mt={2}>
      <Typography variant="h4">Inventory Stems</Typography>
      <ReferenceManyField reference="inventory-stems" target="inventoryPlotUuid">
        <InventoryStemDatagrid />
      </ReferenceManyField>
    </Box>
  </Edit>
);

export const InventoryPlotDatagrid: FC = () => (
  <Datagrid rowClick="edit">
    <TextField source="code" />
    <TextField source="status" />
    <NumberField source="diameter" />
    <NumberField source="hrms" />
    <NumberField source="vrms" />
    <NumberField source="altitude" />
    <NumberField source="utmX" />
    <NumberField source="utmY" />
    <ReferenceField source="forestMassSpeciesUuid" reference="forest-mass-species" label="Species">
      <TextField source="code" />
    </ReferenceField>
    <EditButton />
    <DeleteButton />
  </Datagrid>
);
